import { useRef } from "react";
import { Upload, Trash2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useESGDataEntryStore } from "@/features/esg/hooks/useESGReportStore";
import { useTran } from "@/hooks";
import { observer } from "mobx-react-lite";
import { formatSize } from "@/features/esg/utils/evidence-utils";

const EvidenceUpload = observer(({ entry, code, entryIndex, disabled }) => {
  const { t } = useTran();
  const {
    isUploading,

    uploadFile,
    downloadFile,
    removeFile,
    updateEntry,
  } = useESGDataEntryStore();
  const inputRef = useRef(null);

  const proofs = entry.proofs || [];

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const proof = await uploadFile(file);
    updateEntry(code, entryIndex, "proofs", [...proofs, proof]);
  };

  const handleDelete = async (fileId) => {
    await removeFile(fileId);
    updateEntry(
      code,
      entryIndex,
      "proofs",
      proofs.filter((p) => p.fileId !== fileId),
    );
  };

  return (
    <div className="mt-2 pt-2 border-t border-border">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">
          {t("Evidence / supporting files")}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading || disabled}
        >
          <Upload />
          {isUploading ? t("Uploading...") : t("Upload Evidence")}
        </Button>
      </div>

      {proofs.length > 0 && (
        <div className="space-y-1 mt-2">
          {proofs.map((proof) => (
            <div
              key={proof.fileId}
              className="flex items-center gap-2 bg-muted rounded-md px-2 py-1"
            >
              <span className="text-sm truncate flex-1">{proof.name}</span>
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                {formatSize(proof.size)}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => downloadFile(proof)}
                >
                  <Download size={14} />
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  onClick={() => handleDelete(proof.fileId)}
                  disabled={disabled}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg"
        onChange={handleSelect}
      />
    </div>
  );
});

export default EvidenceUpload;
